import React, { useState, useEffect } from 'react';
import { 
  CreditCard, PackageCheck, Layers, Calendar, CheckCircle, Plus, 
  Edit2, Trash2, X, RefreshCw, AlertCircle, Building2, ShieldCheck, Search,
  Percent, DollarSign, ArrowRight, Check, Globe, ArrowLeft
} from 'lucide-react';
import { AdminService } from '../../services/api';
import ClinicService from '../Clinic/ClinicService';
import PharmacyService from '../Pharmacy/PharmacyService';

const AVAILABLE_MODULES = [
  { key: 'lead_generation', label: 'Patient Leads Ingestion & Routing' },
  { key: 'clinic_cms', label: 'Clinic CMS & OPD Scheduling' },
  { key: 'doctor_cms', label: 'Doctor Teleconsult & EMR' },
  { key: 'pharmacy_cms', label: 'Pharmacy Inventory & Orders' },
  { key: 'lab_cms', label: 'Lab Diagnostics & Test Packages' },
  { key: 'homecare_cms', label: 'Home Healthcare Services' },
];

export default function SubscriptionModuleMaster({ 
  initialTab = 'plans', 
  showCustomAlert, 
  clinics = [], 
  doctors = [],
  pharmacies = [] 
}) {
  const [activeTab, setActiveTab] = useState(initialTab); // 'plans' | 'vendor_subs'
  const [plans, setPlans] = useState([]);
  const [vendorSubs, setVendorSubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('ALL');

  // Vendors list for assigning subscriptions
  const [vendorOptions, setVendorOptions] = useState([]);

  // Modal states for Plans
  const [currencies, setCurrencies] = useState([]);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [planForm, setPlanForm] = useState({
    id: null,
    plan_name: '',
    plan_code: '',
    commercial_model: 'subscription_only', // 'subscription_only' | 'commission_only' | 'hybrid'
    plan_category: 'cms_only',
    billing_cycle: 'monthly',
    base_price: '',
    commission_rate: '',
    commission_type: 'percentage', // 'percentage' | 'fixed'
    currency: 'USD',
    included_modules: [],
    is_active: 1,
    description: '',
  });

  // Modal states for Vendor Subscriptions
  const [showSubModal, setShowSubModal] = useState(false);
  const [subForm, setSubForm] = useState({
    id: null,
    vendor_id: '',
    plan_id: '',
    commercial_model: 'subscription_only',
    business_model: 'cms_only',
    enabled_modules: [],
    subscription_amount: '',
    currency: 'USD',
    commission_rate: '',
    commission_type: 'percentage',
    billing_start_date: new Date().toISOString().slice(0, 10),
    billing_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
    payment_status: 'paid',
    subscription_status: 'active',
  });

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  useEffect(() => {
    loadVendors();
    loadCurrencies();
  }, [clinics, doctors, pharmacies]);

  const loadCurrencies = async () => {
    try {
      const res = await AdminService.getCurrencies();
      if (res.status === 1) {
        setCurrencies(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching currencies:', err);
    }
  };

  const getCurrencySymbol = (code) => {
    const found = currencies.find(c => c.currency_code === code);
    return found ? found.currency_symbol : (code || '$');
  };

  const loadVendors = async () => {
    let combined = [];
    if (clinics && clinics.length > 0) {
      combined = [...combined, ...clinics.map(c => ({ id: c.id, name: c.vendorName || c.vendor_name, type: 'Clinic' }))];
    }
    if (pharmacies && pharmacies.length > 0) {
      combined = [...combined, ...pharmacies.map(p => ({ id: p.id, name: p.vendorName || p.vendor_name, type: 'Pharmacy' }))];
    }
    if (doctors && doctors.length > 0) {
      combined = [...combined, ...doctors.map(d => ({ id: d.id, name: d.vendorName || d.vendor_name, type: 'Doctor' }))];
    }

    if (combined.length === 0) {
      try {
        const [cRes, pRes] = await Promise.allSettled([
          ClinicService.getAllClinics(),
          PharmacyService.getAllPharmacy()
        ]);
        if (cRes.status === 'fulfilled' && cRes.value?.status === 1) {
          const cData = cRes.value.data || [];
          combined = [...combined, ...cData.map(c => ({ id: c.id, name: c.vendor_name || c.vendorName, type: 'Clinic' }))];
        }
        if (pRes.status === 'fulfilled' && pRes.value?.status === 1) {
          const pData = pRes.value.data || [];
          combined = [...combined, ...pData.map(p => ({ id: p.id, name: p.vendor_name || p.vendorName, type: 'Pharmacy' }))];
        }
      } catch (err) {
        console.error('Error fetching vendor options:', err);
      }
    }
    setVendorOptions(combined.filter(v => v.id && v.name));
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'plans') {
        const res = await AdminService.getSubscriptionPlans();
        if (res.status === 1) setPlans(res.data || []);
      } else {
        const res = await AdminService.getVendorModuleSubscriptions();
        if (res.status === 1) setVendorSubs(res.data || []);
      }
    } catch (err) {
      console.error('Error fetching subscription data:', err);
      if (showCustomAlert) showCustomAlert(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // PLAN HANDLERS
  // ==========================================
  const handleOpenAddPlan = () => {
    const defaultCurr = currencies.find(c => c.is_default)?.currency_code || (currencies[0]?.currency_code || 'USD');
    setPlanForm({
      id: null,
      plan_name: '',
      plan_code: '',
      commercial_model: 'subscription_only',
      plan_category: 'cms_only',
      billing_cycle: 'monthly',
      base_price: '',
      commission_rate: '',
      commission_type: 'percentage',
      currency: defaultCurr,
      included_modules: ['clinic_cms', 'doctor_cms'],
      is_active: 1,
      description: '',
    });
    setShowPlanModal(true);
  };

  const handleOpenEditPlan = (plan) => {
    let modules = [];
    try {
      modules = typeof plan.included_modules === 'string' 
        ? JSON.parse(plan.included_modules) 
        : (plan.included_modules || []);
    } catch (e) {
      modules = [];
    }

    setPlanForm({
      id: plan.id,
      plan_name: plan.plan_name || '',
      plan_code: plan.plan_code || '',
      commercial_model: plan.commercial_model || 'subscription_only',
      plan_category: plan.plan_category || 'cms_only',
      billing_cycle: plan.billing_cycle || 'monthly',
      base_price: plan.base_price ?? '',
      commission_rate: plan.commission_rate ?? '',
      commission_type: plan.commission_type || 'percentage',
      currency: plan.currency || 'USD',
      included_modules: Array.isArray(modules) ? modules : [],
      is_active: plan.is_active !== undefined ? Number(plan.is_active) : 1,
      description: plan.description || '',
    });
    setShowPlanModal(true);
  };

  const handleTogglePlanModule = (moduleKey) => {
    setPlanForm(prev => {
      const current = prev.included_modules || [];
      const next = current.includes(moduleKey) ? current.filter(k => k !== moduleKey) : [...current, moduleKey];
      const hasLead = next.includes('lead_generation');
      const hasCms = next.some(key => key !== 'lead_generation');
      return { ...prev, included_modules: next, plan_category: hasLead && hasCms ? 'hybrid' : hasLead ? 'leads_only' : 'cms_only' };
    });
  };

  const handleSavePlan = async (e) => {
    e.preventDefault();
    if (!planForm.plan_name.trim()) {
      if (showCustomAlert) showCustomAlert('Plan name is required');
      return;
    }
    if (!planForm.plan_code.trim()) {
      if (showCustomAlert) showCustomAlert('Plan code is required');
      return;
    }
    if (planForm.commercial_model !== 'commission_only' && (planForm.base_price === '' || isNaN(Number(planForm.base_price)))) {
      if (showCustomAlert) showCustomAlert('Valid base subscription price is required');
      return;
    }
    if (planForm.commercial_model !== 'subscription_only' && (planForm.commission_rate === '' || isNaN(Number(planForm.commission_rate)))) {
      if (showCustomAlert) showCustomAlert('Valid commission rate is required');
      return;
    }
    const hasLead = planForm.included_modules.includes('lead_generation');
    const hasCms = planForm.included_modules.some(key => key !== 'lead_generation');
    if (!hasLead && !hasCms) {
      if (showCustomAlert) showCustomAlert('Select at least one healthcare module');
      return;
    }
    const derivedCategory = hasLead && hasCms ? 'hybrid' : hasLead ? 'leads_only' : 'cms_only';

    try {
      const res = await AdminService.saveSubscriptionPlan({
        ...planForm,
        plan_category: derivedCategory,
        base_price: planForm.commercial_model === 'commission_only' ? 0 : Number(planForm.base_price || 0),
        commission_rate: planForm.commercial_model === 'subscription_only' ? 0 : Number(planForm.commission_rate || 0),
      });
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert(res.msg || 'Subscription plan saved successfully');
        setShowPlanModal(false);
        fetchData();
      } else {
        if (showCustomAlert) showCustomAlert(res.msg || 'Failed to save plan');
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    }
  };

  const handleDeletePlan = async (planId, planName) => {
    if (window.confirm(`Are you sure you want to delete plan "${planName}"? This action cannot be undone.`)) {
      try {
        const res = await AdminService.deleteSubscriptionPlan(planId);
        if (res.status === 1) {
          if (showCustomAlert) showCustomAlert('Subscription plan deleted successfully');
          fetchData();
        } else {
          if (showCustomAlert) showCustomAlert(res.msg || 'Failed to delete plan');
        }
      } catch (err) {
        if (showCustomAlert) showCustomAlert(err.message);
      }
    }
  };

  // ==========================================
  // VENDOR SUBSCRIPTION HANDLERS
  // ==========================================
  const handleOpenAddSub = () => {
    const firstPlan = plans.length > 0 ? plans[0] : null;
    let initialModules = ['clinic_cms', 'doctor_cms'];
    if (firstPlan) {
      try {
        initialModules = typeof firstPlan.included_modules === 'string'
          ? JSON.parse(firstPlan.included_modules)
          : (firstPlan.included_modules || []);
      } catch (e) {
        initialModules = ['clinic_cms', 'doctor_cms'];
      }
    }

    const defaultCurr = firstPlan ? (firstPlan.currency || 'USD') : (currencies.find(c => c.is_default)?.currency_code || 'USD');

    setSubForm({
      id: null,
      vendor_id: vendorOptions.length > 0 ? vendorOptions[0].id : '',
      plan_id: firstPlan ? firstPlan.id : '',
      commercial_model: firstPlan ? (firstPlan.commercial_model || 'subscription_only') : 'subscription_only',
      business_model: firstPlan ? (firstPlan.plan_category || 'cms_only') : 'cms_only',
      enabled_modules: Array.isArray(initialModules) ? initialModules : [],
      subscription_amount: firstPlan ? firstPlan.base_price : 3499,
      currency: defaultCurr,
      commission_rate: firstPlan ? firstPlan.commission_rate : 0,
      commission_type: firstPlan ? (firstPlan.commission_type || 'percentage') : 'percentage',
      billing_start_date: new Date().toISOString().slice(0, 10),
      billing_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
      payment_status: 'paid',
      subscription_status: 'active',
    });
    setShowSubModal(true);
  };

  const handleOpenEditSub = (sub) => {
    let modules = [];
    try {
      modules = typeof sub.enabled_modules === 'string'
        ? JSON.parse(sub.enabled_modules)
        : (sub.enabled_modules || []);
    } catch (e) {
      modules = [];
    }

    setSubForm({
      id: sub.id,
      vendor_id: sub.vendor_id,
      plan_id: sub.plan_id || '',
      commercial_model: sub.commercial_model || sub.plan_commercial_model || 'subscription_only',
      business_model: sub.business_model || 'cms_only',
      enabled_modules: Array.isArray(modules) ? modules : [],
      subscription_amount: sub.subscription_amount ?? '',
      currency: sub.currency || 'USD',
      commission_rate: sub.commission_rate ?? '',
      commission_type: sub.commission_type || 'percentage',
      billing_start_date: sub.billing_start_date ? sub.billing_start_date.toString().slice(0, 10) : '',
      billing_end_date: sub.billing_end_date ? sub.billing_end_date.toString().slice(0, 10) : '',
      payment_status: sub.payment_status || 'paid',
      subscription_status: sub.subscription_status || 'active',
    });
    setShowSubModal(true);
  };

  const handleSelectPlanForSub = (planId) => {
    const selected = plans.find(p => String(p.id) === String(planId));
    if (selected) {
      let modules = [];
      try {
        modules = typeof selected.included_modules === 'string' 
          ? JSON.parse(selected.included_modules) 
          : selected.included_modules;
      } catch (e) {
        modules = [];
      }

      setSubForm(prev => ({
        ...prev,
        plan_id: selected.id,
        commercial_model: selected.commercial_model || 'subscription_only',
        business_model: selected.plan_category || prev.business_model,
        subscription_amount: selected.base_price,
        currency: selected.currency || prev.currency || 'USD',
        commission_rate: selected.commission_rate || 0,
        commission_type: selected.commission_type || 'percentage',
        enabled_modules: Array.isArray(modules) ? modules : [],
      }));
    } else {
      setSubForm(prev => ({ ...prev, plan_id: planId }));
    }
  };

  const handleToggleSubModule = (moduleKey) => {
    setSubForm(prev => {
      if (prev.business_model === 'leads_only' && moduleKey !== 'lead_generation') return prev;
      if (prev.business_model === 'cms_only' && moduleKey === 'lead_generation') return prev;
      const current = prev.enabled_modules || [];
      if (current.includes(moduleKey)) {
        return { ...prev, enabled_modules: current.filter(k => k !== moduleKey) };
      } else {
        return { ...prev, enabled_modules: [...current, moduleKey] };
      }
    });
  };

  const handleBusinessModelChange = (businessModel) => {
    setSubForm(prev => {
      const current = prev.enabled_modules || [];
      const cmsModules = current.filter(key => key !== 'lead_generation');
      return {
        ...prev,
        business_model: businessModel,
        enabled_modules: businessModel === 'leads_only'
          ? ['lead_generation']
          : businessModel === 'cms_only'
            ? cmsModules
            : ['lead_generation', ...cmsModules],
      };
    });
  };

  const handleSaveSub = async (e) => {
    e.preventDefault();
    if (!subForm.vendor_id) {
      if (showCustomAlert) showCustomAlert('Please select a healthcare vendor/organisation');
      return;
    }
    if (!subForm.billing_start_date || !subForm.billing_end_date) {
      if (showCustomAlert) showCustomAlert('Start date and end date are required');
      return;
    }

    try {
      const res = await AdminService.saveVendorModuleSubscription({
        ...subForm,
        vendor_id: Number(subForm.vendor_id),
        plan_id: subForm.plan_id ? Number(subForm.plan_id) : null,
        subscription_amount: Number(subForm.subscription_amount || 0),
        commission_rate: Number(subForm.commission_rate || 0),
      });
      if (res.status === 1) {
        if (showCustomAlert) showCustomAlert('Vendor module subscription allocated successfully');
        setShowSubModal(false);
        fetchData();
      } else {
        if (showCustomAlert) showCustomAlert(res.msg || 'Failed to allocate subscription');
      }
    } catch (err) {
      if (showCustomAlert) showCustomAlert(err.message);
    }
  };

  const handleDeleteSub = async (subId, vendorName) => {
    if (window.confirm(`Are you sure you want to remove subscription for "${vendorName}"?`)) {
      try {
        const res = await AdminService.deleteVendorModuleSubscription(subId);
        if (res.status === 1) {
          if (showCustomAlert) showCustomAlert('Vendor subscription removed successfully');
          fetchData();
        } else {
          if (showCustomAlert) showCustomAlert(res.msg || 'Failed to delete subscription');
        }
      } catch (err) {
        if (showCustomAlert) showCustomAlert(err.message);
      }
    }
  };

  const filteredPlans = plans.filter(p => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = (
      (p.plan_name && p.plan_name.toLowerCase().includes(q)) ||
      (p.plan_code && p.plan_code.toLowerCase().includes(q)) ||
      (p.plan_category && p.plan_category.toLowerCase().includes(q))
    );
    const matchesCurrency = currencyFilter === 'ALL' || (p.currency || 'USD') === currencyFilter;
    return matchesSearch && matchesCurrency;
  });

  const filteredVendorSubs = vendorSubs.filter(vs => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = (
      (vs.vendor_name && vs.vendor_name.toLowerCase().includes(q)) ||
      (vs.plan_name && vs.plan_name.toLowerCase().includes(q)) ||
      (vs.business_model && vs.business_model.toLowerCase().includes(q)) ||
      (vs.subscription_status && vs.subscription_status.toLowerCase().includes(q))
    );
    const matchesCurrency = currencyFilter === 'ALL' || (vs.currency || 'USD') === currencyFilter;
    return matchesSearch && matchesCurrency;
  });

  // -------------------------------------------------------------
  // FULL SCREEN VIEW 1: CREATE / EDIT SUBSCRIPTION PLAN (NO SCROLL)
  // -------------------------------------------------------------
  if (showPlanModal) {
    return (
      <div className="leads-management-wrapper" style={{ height: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#ffffff', padding: '0.7rem 1.25rem', borderRadius: '12px',
          border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
          marginBottom: '0.7rem', flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <button type="button" onClick={() => setShowPlanModal(false)} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#f8fafc', border: '1px solid #cbd5e1', padding: '0.4rem 0.85rem', borderRadius: '7px', color: '#334155', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
              <ArrowLeft size={14} /> Back to Plans
            </button>
            <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: '0.85rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                <CreditCard size={18} color="#0284c7" />
                {planForm.id ? `Edit Plan: ${planForm.plan_name || 'Pricing Tier'}` : 'Create New Pricing Tier'}
              </h2>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button type="button" onClick={() => setShowPlanModal(false)} style={{ padding: '0.4rem 1rem', borderRadius: '7px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
            <button type="button" onClick={handleSavePlan} style={{ background: '#0284c7', color: '#fff', padding: '0.4rem 1.25rem', borderRadius: '7px', border: 'none', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 6px rgba(2,132,199,0.25)' }}>
              <Check size={14} /> {planForm.id ? 'Update Plan' : 'Save Plan'}
            </button>
          </div>
        </div>

        {/* 2-Column Grid fills remaining height */}
        <form onSubmit={handleSavePlan} style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '0.7rem', flex: 1, minHeight: 0 }}>

          {/* LEFT COLUMN */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', minHeight: 0 }}>
            {/* Box A: Plan Identity */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '0.85rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', flexShrink: 0 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.55rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.35rem' }}>
                1. Plan Identification &amp; Operational Currency
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 0.9fr 0.9fr', gap: '0.6rem' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>Plan Name *</label>
                  <input type="text" required placeholder="e.g. Clinic CMS Pro" value={planForm.plan_name}
                    onChange={e => setPlanForm({ ...planForm, plan_name: e.target.value })}
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem' }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>Plan Code *</label>
                  <input type="text" required placeholder="PLAN_PRO" value={planForm.plan_code}
                    onChange={e => setPlanForm({ ...planForm, plan_code: e.target.value.toUpperCase() })}
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', fontFamily: 'monospace', fontWeight: 700 }} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>Currency *</label>
                  <select value={planForm.currency} onChange={e => setPlanForm({ ...planForm, currency: e.target.value })}
                    style={{ width: '100%', padding: '0.45rem 0.4rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, background: '#fff' }}>
                    {currencies.map(c => (<option key={c.currency_code} value={c.currency_code}>{c.currency_code} ({c.currency_symbol})</option>))}
                  </select>
                </div>
              </div>
            </div>

            {/* Box B: Monetisation — flex:1 fills remaining left column */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '0.85rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.55rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.35rem' }}>
                2. Commercial Monetisation Structure
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.65rem' }}>
                <div onClick={() => setPlanForm({ ...planForm, commercial_model: 'subscription_only', commission_rate: 0 })}
                  style={{ padding: '0.7rem', borderRadius: '8px', border: planForm.commercial_model === 'subscription_only' ? '2px solid #0284c7' : '1px solid #e2e8f0', background: planForm.commercial_model === 'subscription_only' ? '#f0f9ff' : '#f8fafc', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                    <strong style={{ fontSize: '0.83rem', color: '#0f172a' }}>Subscription Only</strong>
                    <input type="radio" checked={planForm.commercial_model === 'subscription_only'} onChange={() => {}} style={{ accentColor: '#0284c7' }} />
                  </div>
                  <span style={{ fontSize: '0.71rem', color: '#64748b', display: 'block' }}>Flat monthly/annual fee. Zero booking commissions.</span>
                </div>
                <div onClick={() => setPlanForm({ ...planForm, commercial_model: 'commission_only', base_price: 0 })}
                  style={{ padding: '0.7rem', borderRadius: '8px', border: planForm.commercial_model === 'commission_only' ? '2px solid #d97706' : '1px solid #e2e8f0', background: planForm.commercial_model === 'commission_only' ? '#fffbeb' : '#f8fafc', cursor: 'pointer' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                    <strong style={{ fontSize: '0.83rem', color: '#0f172a' }}>Commission Only</strong>
                    <input type="radio" checked={planForm.commercial_model === 'commission_only'} onChange={() => {}} style={{ accentColor: '#d97706' }} />
                  </div>
                  <span style={{ fontSize: '0.71rem', color: '#64748b', display: 'block' }}>Zero monthly fee. Percentage/fixed cut per booking.</span>
                </div>
              </div>

              {planForm.commercial_model === 'subscription_only' ? (
                <div style={{ background: '#f0f9ff', padding: '0.7rem', borderRadius: '8px', border: '1px solid #bae6fd', marginBottom: '0.65rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <div>
                      <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', display: 'block', marginBottom: '0.2rem' }}>Billing Cycle *</label>
                      <select value={planForm.billing_cycle} onChange={e => setPlanForm({ ...planForm, billing_cycle: e.target.value })}
                        style={{ width: '100%', padding: '0.45rem', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}>
                        <option value="monthly">Monthly Recurring (30 Days)</option>
                        <option value="quarterly">Quarterly (90 Days)</option>
                        <option value="annual">Annual / Yearly (365 Days)</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', display: 'block', marginBottom: '0.2rem' }}>Flat Fee ({planForm.currency || 'USD'}) *</label>
                      <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '9px', top: '7px', fontWeight: 800, color: '#0284c7', fontSize: '0.85rem' }}>{getCurrencySymbol(planForm.currency)}</span>
                        <input type="number" required min="0" step="0.01" placeholder="2999" value={planForm.base_price}
                          onChange={e => setPlanForm({ ...planForm, base_price: e.target.value })}
                          style={{ width: '100%', padding: '0.45rem 0.6rem 0.45rem 2.1rem', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, background: '#fff' }} />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ background: '#fffbeb', padding: '0.7rem', borderRadius: '8px', border: '1px solid #fde68a', marginBottom: '0.65rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
                    <div>
                      <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400e', display: 'block', marginBottom: '0.2rem' }}>Cut Type *</label>
                      <select value={planForm.commission_type} onChange={e => setPlanForm({ ...planForm, commission_type: e.target.value })}
                        style={{ width: '100%', padding: '0.45rem', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}>
                        <option value="percentage">Percentage Rate (%)</option>
                        <option value="fixed">Fixed Cut ({getCurrencySymbol(planForm.currency)})</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#92400e', display: 'block', marginBottom: '0.2rem' }}>Cut Amount *</label>
                      <div style={{ position: 'relative' }}>
                        <input type="number" required min="0" step="0.01" placeholder={planForm.commission_type === 'percentage' ? '10' : '50'} value={planForm.commission_rate}
                          onChange={e => setPlanForm({ ...planForm, commission_rate: e.target.value })}
                          style={{ width: '100%', padding: '0.45rem 2.1rem 0.45rem 0.6rem', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, background: '#fff' }} />
                        <span style={{ position: 'absolute', right: '9px', top: '7px', fontWeight: 800, color: '#b45309', fontSize: '0.85rem' }}>
                          {planForm.commission_type === 'percentage' ? '%' : getCurrencySymbol(planForm.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Status & Notes — pinned to bottom via margin-top:auto */}
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '0.6rem', marginTop: 'auto' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>Status</label>
                  <select value={planForm.is_active} onChange={e => setPlanForm({ ...planForm, is_active: Number(e.target.value) })}
                    style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem' }}>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>Plan Notes / Description</label>
                  <input type="text" placeholder="Brief description for billing cards" value={planForm.description}
                    onChange={e => setPlanForm({ ...planForm, description: e.target.value })}
                    style={{ width: '100%', padding: '0.45rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem' }} />
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Modules — fills full height */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.55rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.35rem', flexShrink: 0 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a' }}>
                3. Unlocked Healthcare Modules ({planForm.included_modules.length} selected)
              </span>
              <button type="button"
                onClick={() => {
                  const allKeys = AVAILABLE_MODULES.map(m => m.key);
                  const next = planForm.included_modules.length === allKeys.length ? [] : allKeys;
                  const hasLead = next.includes('lead_generation');
                  const hasCms = next.some(key => key !== 'lead_generation');
                  setPlanForm({ ...planForm, included_modules: next, plan_category: hasLead && hasCms ? 'hybrid' : hasLead ? 'leads_only' : 'cms_only' });
                }}
                style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}>
                {planForm.included_modules.length === AVAILABLE_MODULES.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Module rows stretch to fill height */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '0.4rem', minHeight: 0 }}>
              {AVAILABLE_MODULES.map(m => {
                const checked = (planForm.included_modules || []).includes(m.key);
                return (
                  <label key={m.key} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0 0.85rem', flex: 1, borderRadius: '8px',
                    background: checked ? '#f0f9ff' : '#f8fafc',
                    border: checked ? '1.5px solid #0284c7' : '1px solid #e2e8f0',
                    fontSize: '0.82rem', fontWeight: checked ? 700 : 500,
                    color: checked ? '#0f172a' : '#475569',
                    cursor: 'pointer', transition: 'all 0.12s ease', minHeight: '2.4rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      <input type="checkbox" checked={checked} onChange={() => handleTogglePlanModule(m.key)}
                        style={{ width: 15, height: 15, accentColor: '#0284c7', cursor: 'pointer' }} />
                      <span>{m.label}</span>
                    </div>
                    {checked && <CheckCircle size={15} color="#0284c7" />}
                  </label>
                );
              })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.65rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.65rem', marginTop: '0.65rem', flexShrink: 0 }}>
              <button type="button" onClick={() => setShowPlanModal(false)} style={{ padding: '0.45rem 1.1rem', borderRadius: '7px', border: '1px solid #cbd5e1', background: '#fff', color: '#475569', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
              <button type="submit" style={{ background: '#0284c7', color: '#fff', padding: '0.45rem 1.5rem', borderRadius: '7px', border: 'none', fontWeight: 800, fontSize: '0.82rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.4rem', boxShadow: '0 2px 8px rgba(2,132,199,0.25)' }}>
                <Check size={15} /> {planForm.id ? 'Save & Update Plan' : 'Create & Publish Plan'}
              </button>
            </div>
          </div>

        </form>
      </div>
    );
  }



  // -------------------------------------------------------------
  // FULL SCREEN VIEW 2: ASSIGN / EDIT VENDOR MODULE SUBSCRIPTION (NO SCROLL)
  // -------------------------------------------------------------
  if (showSubModal) {
    return (
      <div className="leads-management-wrapper" style={{ height: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#ffffff',
          padding: '0.7rem 1.25rem',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
          marginBottom: '0.7rem',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
            <button
              type="button"
              onClick={() => setShowSubModal(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                padding: '0.45rem 0.85rem',
                borderRadius: '7px',
                color: '#334155',
                fontSize: '0.82rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              <ArrowLeft size={14} /> Back to Subscriptions
            </button>
            <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: '0.85rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                <PackageCheck size={18} color="#059669" />
                {subForm.id ? 'Edit Vendor Module Subscription' : 'Assign Module Subscription to Vendor'}
              </h2>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <button
              type="button"
              onClick={() => setShowSubModal(false)}
              style={{
                padding: '0.45rem 1rem',
                borderRadius: '7px',
                border: '1px solid #cbd5e1',
                background: '#fff',
                color: '#475569',
                fontWeight: 600,
                fontSize: '0.82rem',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveSub}
              style={{
                background: '#059669',
                color: '#fff',
                padding: '0.45rem 1.25rem',
                borderRadius: '7px',
                border: 'none',
                fontWeight: 700,
                fontSize: '0.82rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.4rem',
                boxShadow: '0 2px 6px rgba(5, 150, 105, 0.25)'
              }}
            >
              <Check size={14} /> {subForm.id ? 'Update Allocation' : 'Confirm Allocation'}
            </button>
          </div>
        </div>

        {/* 2-Column Responsive Compact Layout */}
        <form onSubmit={handleSaveSub} style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: '0.7rem', flex: 1, minHeight: 0 }}>
          
          {/* LEFT COLUMN: Vendor, Plan Tier, Access Model & Dates */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem', minHeight: 0 }}>
            {/* Box 1: Vendor & Tier */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
              <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.65rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.4rem' }}>
                1. Organisation & Package Tier
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.25rem' }}>
                    Healthcare Organisation / Vendor *
                  </label>
                  <select
                    required
                    disabled={!!subForm.id}
                    value={subForm.vendor_id}
                    onChange={e => setSubForm({ ...subForm, vendor_id: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 600, background: subForm.id ? '#f1f5f9' : '#fff' }}
                  >
                    <option value="">-- Choose Organisation --</option>
                    {vendorOptions.map(v => (
                      <option key={v.id} value={v.id}>
                        {v.name} ({v.type}) - ID #{v.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.65rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.25rem' }}>
                      Subscription Plan Tier
                    </label>
                    <select
                      value={subForm.plan_id}
                      onChange={e => handleSelectPlanForSub(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}
                    >
                      <option value="">-- Custom Allocation --</option>
                      {plans.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.plan_name} ({getCurrencySymbol(p.currency)}{Number(p.base_price || 0).toLocaleString()} / {p.billing_cycle})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.25rem' }}>
                      Business Access Model
                    </label>
                    <select
                      value={subForm.business_model}
                      onChange={e => handleBusinessModelChange(e.target.value)}
                      style={{ width: '100%', padding: '0.5rem 0.65rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}
                    >
                      <option value="leads_only">Lead Generation Only</option>
                      <option value="cms_only">CMS Only</option>
                      <option value="hybrid">Hybrid (Leads + CMS)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Box 2: Monetisation Terms, Dates & Status */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '1rem', flex: 1, boxShadow: '0 1px 3px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.65rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.4rem' }}>
                  2. Commercial Monetisation Terms & Rates
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.25rem' }}>
                      Commercial Model *
                    </label>
                    <select
                      value={subForm.commercial_model}
                      onChange={e => setSubForm({ ...subForm, commercial_model: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}
                    >
                      <option value="subscription_only">Subscription Only (Flat Fee)</option>
                      <option value="commission_only">Commission Only (% or Fixed)</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.25rem' }}>
                      Operational Currency *
                    </label>
                    <select
                      value={subForm.currency}
                      onChange={e => setSubForm({ ...subForm, currency: e.target.value })}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.82rem', fontWeight: 700, background: '#fff' }}
                    >
                      {currencies.map(c => (
                        <option key={c.currency_code} value={c.currency_code}>
                          {c.currency_code} ({c.currency_symbol})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Pricing / Cut Input */}
                <div style={{ background: subForm.commercial_model !== 'commission_only' ? '#f0f9ff' : '#fffbeb', padding: '0.75rem', borderRadius: '8px', border: `1px solid ${subForm.commercial_model !== 'commission_only' ? '#bae6fd' : '#fde68a'}`, marginBottom: '0.75rem' }}>
                  {subForm.commercial_model !== 'commission_only' ? (
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0369a1', display: 'block', marginBottom: '0.25rem' }}>
                        Subscription Fee ({getCurrencySymbol(subForm.currency)}) *
                      </label>
                      <input 
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="2999"
                        value={subForm.subscription_amount}
                        onChange={e => setSubForm({ ...subForm, subscription_amount: e.target.value })}
                        style={{ width: '100%', padding: '0.45rem 0.65rem', border: '1px solid #7dd3fc', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, background: '#fff' }}
                      />
                    </div>
                  ) : (
                    <div>
                      <label style={{ fontSize: '0.75rem', fontWeight: 700, color: '#92400e', display: 'block', marginBottom: '0.25rem' }}>
                        Commission Rate per Booking *
                      </label>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <select
                          value={subForm.commission_type}
                          onChange={e => setSubForm({ ...subForm, commission_type: e.target.value })}
                          style={{ width: '45%', padding: '0.45rem', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.82rem', background: '#fff' }}
                        >
                          <option value="percentage">% Cut</option>
                          <option value="fixed">{getCurrencySymbol(subForm.currency)} Fixed</option>
                        </select>
                        <input 
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="10"
                          value={subForm.commission_rate}
                          onChange={e => setSubForm({ ...subForm, commission_rate: e.target.value })}
                          style={{ width: '55%', padding: '0.45rem 0.65rem', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 700, background: '#fff' }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Dates & Status */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem', marginBottom: '0.65rem' }}>
                  <div>
                    <label style={{ fontSize: '0.73rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>
                      Start Date *
                    </label>
                    <input 
                      type="date"
                      required
                      value={subForm.billing_start_date}
                      onChange={e => setSubForm({ ...subForm, billing_start_date: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: '0.73rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>
                      End Date *
                    </label>
                    <input 
                      type="date"
                      required
                      value={subForm.billing_end_date}
                      onChange={e => setSubForm({ ...subForm, billing_end_date: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                  <div>
                    <label style={{ fontSize: '0.73rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>
                      Payment Status
                    </label>
                    <select
                      value={subForm.payment_status}
                      onChange={e => setSubForm({ ...subForm, payment_status: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem' }}
                    >
                      <option value="paid">Paid</option>
                      <option value="pending">Pending</option>
                      <option value="overdue">Overdue</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.73rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '0.2rem' }}>
                      Subscription Status
                    </label>
                    <select
                      value={subForm.subscription_status}
                      onChange={e => setSubForm({ ...subForm, subscription_status: e.target.value })}
                      style={{ width: '100%', padding: '0.45rem', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '0.8rem' }}
                    >
                      <option value="active">Active</option>
                      <option value="expiring">Expiring</option>
                      <option value="expired">Expired</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN: Active Module Permissions */}
          <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', boxShadow: '0 1px 3px rgba(0,0,0,0.02)', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.55rem', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.35rem', flexShrink: 0 }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a' }}>
                3. Active Module Permissions ({subForm.enabled_modules.length} enabled)
              </span>
              <button
                type="button"
                onClick={() => {
                  const allKeys = AVAILABLE_MODULES
                    .map(m => m.key)
                    .filter(key => subForm.business_model !== 'leads_only' || key === 'lead_generation')
                    .filter(key => subForm.business_model !== 'cms_only' || key !== 'lead_generation');
                  setSubForm({ ...subForm, enabled_modules: subForm.enabled_modules.length === allKeys.length ? [] : allKeys });
                }}
                style={{ background: 'none', border: 'none', color: '#059669', fontSize: '0.74rem', fontWeight: 700, cursor: 'pointer' }}
              >
                {subForm.enabled_modules.length === AVAILABLE_MODULES.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>

            {/* Module rows — flex:1 fills height evenly */}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '0.4rem', minHeight: 0 }}>
              {AVAILABLE_MODULES.map(m => {
                const checked = (subForm.enabled_modules || []).includes(m.key);
                const disabled = (subForm.business_model === 'leads_only' && m.key !== 'lead_generation') ||
                  (subForm.business_model === 'cms_only' && m.key === 'lead_generation');
                return (
                  <label
                    key={m.key}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0 0.85rem', flex: 1, borderRadius: '8px',
                      background: checked ? '#ecfdf5' : '#f8fafc',
                      border: checked ? '1.5px solid #059669' : '1px solid #e2e8f0',
                      fontSize: '0.82rem', fontWeight: checked ? 700 : 500,
                      color: checked ? '#0f172a' : '#475569',
                      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1, transition: 'all 0.12s ease', minHeight: '2.4rem'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem' }}>
                      <input type="checkbox" checked={checked} disabled={disabled} onChange={() => handleToggleSubModule(m.key)}
                        style={{ width: 15, height: 15, accentColor: '#059669', cursor: 'pointer' }} />
                      <span>{m.label}</span>
                    </div>
                    {checked && <CheckCircle size={15} color="#059669" />}
                  </label>
                );
              })}
            </div>

            {/* Bottom Actions inside the card */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.65rem', borderTop: '1px solid #f1f5f9', paddingTop: '0.75rem', marginTop: '0.75rem' }}>
              <button
                type="button"
                onClick={() => setShowSubModal(false)}
                style={{
                  padding: '0.5rem 1.1rem',
                  borderRadius: '7px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  color: '#475569',
                  fontWeight: 600,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  background: '#059669',
                  color: '#fff',
                  padding: '0.5rem 1.5rem',
                  borderRadius: '7px',
                  border: 'none',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  boxShadow: '0 2px 8px rgba(5, 150, 105, 0.25)'
                }}
              >
                <Check size={15} /> {subForm.id ? 'Update Allocation' : 'Confirm Allocation'}
              </button>
            </div>
          </div>

        </form>
      </div>
    );
  }

  return (
    <div className="leads-management-wrapper">
      {/* Tab Navigation */}
      <div className="leads-subtabs-nav">
        <button 
          className={`leads-tab-btn ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => { setActiveTab('plans'); setSearchTerm(''); }}
        >
          <CreditCard size={16} /> Subscription Plans & Pricing ({plans.length})
        </button>
        <button 
          className={`leads-tab-btn ${activeTab === 'vendor_subs' ? 'active' : ''}`}
          onClick={() => { setActiveTab('vendor_subs'); setSearchTerm(''); }}
        >
          <PackageCheck size={16} /> Vendor Module Subscriptions ({vendorSubs.length})
        </button>
      </div>

      {/* Control bar */}
      <div className="leads-controls-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: '1 1 340px' }}>
          <div className="leads-search-box" style={{ flex: 1, minWidth: '220px' }}>
            <Search size={16} className="search-icon" />
            <input 
              type="text" 
              placeholder={activeTab === 'plans' ? 'Search plans by name, code or category...' : 'Search vendor subscriptions by organisation, plan or status...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* CURRENCY FILTER DROPDOWN */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: '#fff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0.4rem 0.65rem' }}>
            <Globe size={15} color="#2563eb" />
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>Currency:</span>
            <select
              value={currencyFilter}
              onChange={(e) => setCurrencyFilter(e.target.value)}
              style={{
                border: 'none',
                background: 'transparent',
                fontSize: '0.82rem',
                fontWeight: 700,
                color: '#0f172a',
                cursor: 'pointer',
                outline: 'none',
                paddingRight: '4px'
              }}
            >
              <option value="ALL">All Currencies</option>
              {currencies.map(c => (
                <option key={c.currency_code} value={c.currency_code}>
                  {c.currency_code} ({c.currency_symbol})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="leads-filters" style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button className="leads-refresh-btn" onClick={fetchData} title="Refresh">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {activeTab === 'plans' ? (
            <button 
              className="view-lead-btn" 
              style={{ background: '#0284c7', color: '#fff', padding: '0.6rem 1rem', fontSize: '0.85rem' }}
              onClick={handleOpenAddPlan}
            >
              <Plus size={16} /> Create New Plan
            </button>
          ) : (
            <button 
              className="view-lead-btn" 
              style={{ background: '#059669', color: '#fff', padding: '0.6rem 1rem', fontSize: '0.85rem' }}
              onClick={handleOpenAddSub}
            >
              <Plus size={16} /> Assign Plan to Vendor
            </button>
          )}
        </div>
      </div>

      {/* TAB 1: SUBSCRIPTION PLANS */}
      {activeTab === 'plans' ? (
        <div className="leads-table-card">
          <div style={{ padding: '1.25rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Subscription Plans Catalogue</h3>
              <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Manage pricing tiers for Lead Generation Only, CMS Only, or Full Hybrid Healthcare Suite.
              </p>
            </div>
          </div>

          <table className="leads-table">
            <thead>
              <tr>
                <th>Plan Name</th>
                <th>Plan Code</th>
                <th>Monetisation Model</th>
                <th>Category</th>
                <th>Cycle / Pricing</th>
                <th>Included Modules</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <CreditCard size={32} color="#94a3b8" />
                      <strong>No subscription plans found</strong>
                      <p style={{ margin: 0, fontSize: '0.85rem' }}>Click "Create New Plan" to configure your first subscription tier.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredPlans.map((p) => {
                  let modules = [];
                  try {
                    modules = typeof p.included_modules === 'string' ? JSON.parse(p.included_modules) : p.included_modules;
                  } catch (e) {
                    modules = [];
                  }

                  const isSubOnly = p.commercial_model !== 'commission_only';
                  const isCommOnly = p.commercial_model === 'commission_only';

                  return (
                    <tr key={p.id}>
                      <td>
                        <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>{p.plan_name}</strong>
                        {p.description && (
                          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '2px', maxWidth: '280px' }}>
                            {p.description}
                          </div>
                        )}
                      </td>
                      <td><code>{p.plan_code}</code></td>
                      <td>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.35rem',
                          padding: '0.3rem 0.65rem',
                          borderRadius: '999px',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          background: isSubOnly ? '#eff6ff' : '#fffbeb',
                          color: isSubOnly ? '#1d4ed8' : '#b45309',
                          border: `1px solid ${isSubOnly ? '#bfdbfe' : '#fde68a'}`
                        }}>
                          {isSubOnly ? <CreditCard size={12} /> : <Percent size={12} />}
                          {isSubOnly ? 'Subscription Only' : 'Commission Only'}
                        </span>
                      </td>
                      <td>
                        <span className={`service-tag tag-${p.plan_category}`}>
                          {p.plan_category === 'leads_only' && 'Leads Only'}
                          {p.plan_category === 'cms_only' && 'CMS Only'}
                          {p.plan_category === 'hybrid' && 'Hybrid Suite'}
                        </span>
                      </td>
                      <td>
                        {isSubOnly ? (
                          <div>
                            <strong style={{ fontSize: '0.9rem', color: '#0f172a' }}>
                              {getCurrencySymbol(p.currency)}{Number(p.base_price).toLocaleString()}
                            </strong>
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}> / {p.billing_cycle}</span>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{p.currency || 'USD'}</div>
                          </div>
                        ) : (
                          <div>
                            <strong style={{ fontSize: '0.9rem', color: '#b45309' }}>
                              {Number(p.commission_rate)} {p.commission_type === 'percentage' ? '%' : `${getCurrencySymbol(p.currency)} fixed`}
                            </strong>
                            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>per booking ({p.currency || 'USD'})</div>
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '280px' }}>
                          {Array.isArray(modules) && modules.map((m) => (
                            <span key={m} style={{ fontSize: '0.7rem', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                              {m.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <span style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          gap: '4px', 
                          color: p.is_active ? '#059669' : '#dc2626', 
                          fontWeight: 600,
                          fontSize: '0.8rem'
                        }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.is_active ? '#059669' : '#dc2626' }}></span>
                          {p.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button 
                            className="view-lead-btn" 
                            style={{ padding: '0.35rem 0.6rem' }}
                            onClick={() => handleOpenEditPlan(p)}
                            title="Edit Plan"
                          >
                            <Edit2 size={13} /> Edit
                          </button>
                          <button 
                            className="view-lead-btn" 
                            style={{ padding: '0.35rem 0.6rem', color: '#dc2626', borderColor: '#fca5a5' }}
                            onClick={() => handleDeletePlan(p.id, p.plan_name)}
                            title="Delete Plan"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        /* TAB 2: VENDOR MODULE SUBSCRIPTIONS */
        <div className="leads-table-card">
          <div style={{ padding: '1.25rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#0f172a' }}>Subscribed Healthcare Organisations</h3>
              <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                Monitors active module allocations, billing validity, and renewal status across Clinics, Pharmacies, Labs, and Home Care.
              </p>
            </div>
          </div>

          <table className="leads-table">
            <thead>
              <tr>
                <th>Healthcare Organisation</th>
                <th>Subscribed Plan</th>
                <th>Monetisation Model</th>
                <th>Access Model</th>
                <th>Active Modules</th>
                <th>Validity Period</th>
                <th>Pricing / Terms</th>
                <th>Payment</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVendorSubs.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{ textAlign: 'center', padding: '2.5rem', color: '#64748b' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                      <PackageCheck size={32} color="#94a3b8" />
                      <strong>No vendor module subscriptions found</strong>
                      <p style={{ margin: 0, fontSize: '0.85rem' }}>Click "Assign Plan to Vendor" to grant modules to an onboarded organisation.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredVendorSubs.map((vs) => {
                  let modules = [];
                  try {
                    modules = typeof vs.enabled_modules === 'string' ? JSON.parse(vs.enabled_modules) : vs.enabled_modules;
                  } catch (e) {
                    modules = [];
                  }

                  const model = vs.commercial_model || vs.plan_commercial_model || 'subscription_only';
                  const isSub = model === 'subscription_only';
                  const isComm = model === 'commission_only';
                  const isHyb = model === 'hybrid';

                  return (
                    <tr key={vs.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <Building2 size={16} color="#0284c7" />
                          <div>
                            <strong>{vs.vendor_name || `Vendor #${vs.vendor_id}`}</strong>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>ID #{vs.vendor_id}</div>
                          </div>
                        </div>
                      </td>
                      <td><strong>{vs.plan_name || 'Custom Suite'}</strong></td>
                      <td>
                        <span style={{
                          padding: '0.2rem 0.55rem',
                          borderRadius: '6px',
                          fontSize: '0.72rem',
                          fontWeight: 700,
                          background: isSub ? '#eff6ff' : isComm ? '#fef3c7' : '#f3e8ff',
                          color: isSub ? '#1d4ed8' : isComm ? '#b45309' : '#7e22ce',
                          border: `1px solid ${isSub ? '#bfdbfe' : isComm ? '#fde68a' : '#e9d5ff'}`
                        }}>
                          {isSub ? 'Subscription Only' : isComm ? 'Commission Only' : 'Hybrid'}
                        </span>
                      </td>
                      <td>
                        <span className={`delivery-badge badge-${vs.business_model}`}>
                          {vs.business_model}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', maxWidth: '240px' }}>
                          {Array.isArray(modules) && modules.map((m) => (
                            <span key={m} style={{ fontSize: '0.7rem', background: '#f8fafc', padding: '2px 5px', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                              {m.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.8rem' }}>
                          {vs.billing_start_date ? new Date(vs.billing_start_date).toLocaleDateString() : 'N/A'} 
                          <span style={{ color: '#94a3b8' }}> to </span>
                          {vs.billing_end_date ? new Date(vs.billing_end_date).toLocaleDateString() : 'N/A'}
                        </div>
                      </td>
                      <td>
                        {isSub && (
                          <div>
                            <strong>{getCurrencySymbol(vs.currency)}{Number(vs.subscription_amount).toLocaleString()}</strong>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{vs.currency || 'USD'}</div>
                          </div>
                        )}
                        {isComm && (
                          <div>
                            <strong style={{ color: '#b45309' }}>
                              {Number(vs.commission_rate)} {vs.commission_type === 'percentage' ? '%' : `${getCurrencySymbol(vs.currency)} fixed`}
                            </strong>
                            <div style={{ fontSize: '0.7rem', color: '#64748b' }}>per booking ({vs.currency || 'USD'})</div>
                          </div>
                        )}
                        {isHyb && (
                          <div>
                            <strong>{getCurrencySymbol(vs.currency)}{Number(vs.subscription_amount).toLocaleString()}</strong>
                            <div style={{ fontSize: '0.72rem', color: '#7e22ce' }}>
                              + {Number(vs.commission_rate)}{vs.commission_type === 'percentage' ? '%' : getCurrencySymbol(vs.currency)} cut
                            </div>
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`state-badge state-${vs.payment_status === 'paid' ? 'success' : 'failed'}`}>
                          {vs.payment_status}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          padding: '0.2rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          background: vs.subscription_status === 'active' ? '#ecfdf5' : '#fef2f2',
                          color: vs.subscription_status === 'active' ? '#047857' : '#b91c1c'
                        }}>
                          {vs.subscription_status}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                          <button 
                            className="view-lead-btn" 
                            style={{ padding: '0.35rem 0.6rem' }}
                            onClick={() => handleOpenEditSub(vs)}
                            title="Edit Subscription"
                          >
                            <Edit2 size={13} /> Edit
                          </button>
                          <button 
                            className="view-lead-btn" 
                            style={{ padding: '0.35rem 0.6rem', color: '#dc2626', borderColor: '#fca5a5' }}
                            onClick={() => handleDeleteSub(vs.id, vs.vendor_name || `Vendor #${vs.vendor_id}`)}
                            title="Delete Subscription"
                          >
                            <Trash2 size={13} /> Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

